import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Flex, VStack, HStack, Text, Heading, Button,
    Input, FormControl, FormLabel, RadioGroup, Radio,
    Spinner, Icon, useToast, Divider, Badge, Image
} from '@chakra-ui/react';
import { MdCalendarMonth, MdCheckCircle, MdSend } from 'react-icons/md';
import axios from 'axios';

const BASE = 'https://api.cobra-education.com/api';
const PRIMARY = '#F2C919';
const DARK = '#2C3E50';

export default function ScheduleFormPublic() {
    const { id } = useParams();
    const toast = useToast();

    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState('');

    const [fullName, setFullName] = useState('');
    const [whatsappNumber, setWhatsappNumber] = useState('');
    const [session1Choice, setSession1Choice] = useState('');
    const [session2Choice, setSession2Choice] = useState('');

    useEffect(() => {
        axios.get(`${BASE}/schedule-forms/${id}`)
            .then(res => setForm(res.data.data))
            .catch(() => setError('الاستمارة غير موجودة أو تم حذفها'))
            .finally(() => setLoading(false));
    }, [id]);

    const handleSubmit = async () => {
        if (!fullName.trim() || !whatsappNumber.trim() || !session1Choice || !session2Choice) {
            return toast({ title: 'يرجى ملء جميع الحقول', status: 'warning', duration: 3000, isClosable: true });
        }
        setSubmitting(true);
        try {
            await axios.post(`${BASE}/schedule-forms/${id}/submit`, {
                fullName: fullName.trim(),
                whatsappNumber: whatsappNumber.trim(),
                session1Choice,
                session2Choice,
            });
            setSubmitted(true);
        } catch (e) {
            toast({
                title: 'خطأ',
                description: e.response?.data?.error || 'حدث خطأ، يرجى المحاولة مجدداً',
                status: 'error', duration: 4000, isClosable: true
            });
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return (
        <Flex minH="100vh" align="center" justify="center" bg="gray.50">
            <Spinner size="xl" color={PRIMARY} thickness="4px" />
        </Flex>
    );

    if (error) return (
        <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={6}>
            <VStack>
                <Icon as={MdCalendarMonth} boxSize={16} color="gray.700" />
                <Text fontSize="xl" color="gray.500">{error}</Text>
            </VStack>
        </Flex>
    );

    if (submitted) return (
        <Flex minH="100vh" align="center" justify="center" bg="gray.50" p={6}>
            <Box bg="white" borderRadius="3xl" shadow="2xl" p={10} maxW="480px" w="100%" textAlign="center">
                <Icon as={MdCheckCircle} boxSize={20} color="green.400" mb={4} />
                <Heading size="lg" color={DARK} mb={3}>تم إرسال إجابتك ✅</Heading>
                <Text color="gray.500" fontSize="md">
                    شكراً {fullName}! تم تسجيل اختيارك بنجاح. سيتم التواصل معك عبر الواتساب.
                </Text>
                <Box mt={6} p={4} bg="yellow.50" borderRadius="xl" border="1px solid" borderColor="yellow.200">
                    <VStack spacing={1}>
                        <Text fontWeight="700" color={DARK}>اختياراتك:</Text>
                        <HStack>
                            <Badge colorScheme="blue" px={3} py={1} borderRadius="full">الحصة الأولى: {session1Choice}</Badge>
                            <Badge colorScheme="purple" px={3} py={1} borderRadius="full">الحصة الثانية: {session2Choice}</Badge>
                        </HStack>
                    </VStack>
                </Box>
            </Box>
        </Flex>
    );

    return (
        <Flex minH="100vh" align="center" justify="center" bg="linear-gradient(135deg, #2C3E50 0%, #3d5166 60%, #2C3E50 100%)" p={4}>
            <Box
                bg="white"
                borderRadius="3xl"
                shadow="2xl"
                p={{ base: 5, md: 8 }}
                maxW="520px"
                w="100%"
                border="3px solid"
                borderColor={PRIMARY}
                dir="rtl"
            >
                {/* Header */}
                <VStack mb={6} spacing={2}>
                    <Box
                        bg={PRIMARY}
                        p={3}
                        borderRadius="2xl"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Icon as={MdCalendarMonth} boxSize={8} color={DARK} />
                    </Box>
                    <Heading size="lg" color={DARK} textAlign="center">
                        اختر جدول اوقات يناسبك
                    </Heading>
                    <Badge colorScheme="yellow" fontSize="md" px={4} py={1} borderRadius="full">
                        {form.matiere}
                    </Badge>
                </VStack>

                <Divider mb={6} />

                <VStack spacing={5} align="stretch">
                    {/* Full Name */}
                    <FormControl isRequired>
                        <FormLabel fontWeight="700" color={DARK}>اسمك و لقبك</FormLabel>
                        <Input
                            value={fullName}
                            onChange={e => setFullName(e.target.value)}
                            placeholder="مثال: محمد بن علي"
                            size="lg"
                            borderRadius="xl"
                            focusBorderColor={PRIMARY}
                            _focus={{ borderColor: PRIMARY, boxShadow: `0 0 0 1px ${PRIMARY}` }}
                        />
                    </FormControl>

                    {/* WhatsApp */}
                    <FormControl isRequired>
                        <FormLabel fontWeight="700" color={DARK}>رقم الواتساب الي داخل بيه فالقروب</FormLabel>
                        <Input
                            value={whatsappNumber}
                            onChange={e => setWhatsappNumber(e.target.value)}
                            placeholder="مثال: 21612345678"
                            size="lg"
                            borderRadius="xl"
                            dir="ltr"
                            type="tel"
                            focusBorderColor={PRIMARY}
                            _focus={{ borderColor: PRIMARY, boxShadow: `0 0 0 1px ${PRIMARY}` }}
                        />
                    </FormControl>

                    {/* Session 1 */}
                    <FormControl isRequired>
                        <FormLabel fontWeight="700" color={DARK}>الحصة الأولى</FormLabel>
                        <RadioGroup value={session1Choice} onChange={setSession1Choice}>
                            <VStack align="stretch" spacing={2}>
                                {form.session1Options.map((opt, i) => (
                                    <Box
                                        key={i}
                                        p={3}
                                        borderRadius="xl"
                                        border="2px solid"
                                        borderColor={session1Choice === opt ? PRIMARY : 'gray.200'}
                                        bg={session1Choice === opt ? 'yellow.50' : 'white'}
                                        cursor="pointer"
                                        onClick={() => setSession1Choice(opt)}
                                        transition="all 0.2s"
                                        _hover={{ borderColor: PRIMARY, bg: 'yellow.50' }}
                                    >
                                        <Radio value={opt} colorScheme="yellow" size="lg">
                                            <Text fontWeight={session1Choice === opt ? '700' : '400'} color={DARK}>{opt}</Text>
                                        </Radio>
                                    </Box>
                                ))}
                            </VStack>
                        </RadioGroup>
                    </FormControl>

                    {/* Session 2 */}
                    <FormControl isRequired>
                        <FormLabel fontWeight="700" color={DARK}>الحصة الثانية</FormLabel>
                        <RadioGroup value={session2Choice} onChange={setSession2Choice}>
                            <VStack align="stretch" spacing={2}>
                                {form.session2Options.map((opt, i) => (
                                    <Box
                                        key={i}
                                        p={3}
                                        borderRadius="xl"
                                        border="2px solid"
                                        borderColor={session2Choice === opt ? PRIMARY : 'gray.200'}
                                        bg={session2Choice === opt ? 'yellow.50' : 'white'}
                                        cursor="pointer"
                                        onClick={() => setSession2Choice(opt)}
                                        transition="all 0.2s"
                                        _hover={{ borderColor: PRIMARY, bg: 'yellow.50' }}
                                    >
                                        <Radio value={opt} colorScheme="yellow" size="lg">
                                            <Text fontWeight={session2Choice === opt ? '700' : '400'} color={DARK}>{opt}</Text>
                                        </Radio>
                                    </Box>
                                ))}
                            </VStack>
                        </RadioGroup>
                    </FormControl>

                    <Button
                        onClick={handleSubmit}
                        isLoading={submitting}
                        loadingText="جاري الإرسال..."
                        size="lg"
                        bg={PRIMARY}
                        color={DARK}
                        fontWeight="800"
                        borderRadius="xl"
                        leftIcon={<MdSend />}
                        _hover={{ bg: '#d4ac00', transform: 'translateY(-1px)', shadow: 'lg' }}
                        transition="all 0.2s"
                    >
                        إرسال الاختيار
                    </Button>
                </VStack>
            </Box>
        </Flex>
    );
}

